import { useCallback, useState } from "react";

import { FormValues, WidgetTypes } from "@/types/widget";
import { showToast } from "@/features/shared/components";
import { useCreateWidgetMutation } from "@/features/admin/features/widgets/mutations";
import { DynamicWidgetDialog } from "@/features/widget-forms/dialogs/dynamic-widget-dialog";
import { PickWidgetDialog } from "@/features/widget-forms/dialogs/pick-widget-type-dialog";
import { WidgetClipboardLayout } from "@/features/admin/features/widgets/widget-clipboard";
import { CreateWidgetBody, WidgetConfiguration } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

interface CreateWidgetDialogProps {
  pageId: string;
  containerId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pre-filled widget values from a paste action. When set, the type picker is
   * skipped and the type-specific form opens pre-filled with these values so
   * the user can review before saving.
   */
  pastedValues?: FormValues;
  /**
   * Source container size from a paste action. Applied only when creating a new
   * container (no `containerId`) so the pasted widget keeps its original size;
   * pasting into an existing container inherits that container's dimensions.
   */
  pastedLayout?: WidgetClipboardLayout;
}

const CreateWidgetDialog = ({
  pageId,
  containerId,
  open,
  onOpenChange,
  pastedValues,
  pastedLayout,
}: CreateWidgetDialogProps) => {
  const { t } = useTranslation();
  const [pickedType, setPickedType] = useState<WidgetTypes>();

  const createWidgetMutation = useCreateWidgetMutation();

  const pastedType = pastedValues?.configuration?.type as WidgetTypes | undefined;
  const widgetType = pastedValues ? pastedType : pickedType;

  async function handleSubmit(values: FormValues) {
    await createWidgetMutation.mutateAsync({
      pageId,
      data: {
        widget_container_id: containerId,
        ...values,
        // A pasted size only makes sense for a brand-new container; when adding
        // into an existing container the backend keeps that container's size.
        ...(pastedLayout && !containerId
          ? { width: pastedLayout.width, height: pastedLayout.height }
          : {}),
      } as CreateWidgetBody,
    });
    showToast(t("admin.messages.widget_created"));
    onOpenChange?.(false);
  }

  const handleReset = useCallback(() => {
    setPickedType(undefined);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const initialValues = pastedValues ?? {
    title: t("admin.widgets.fields.name.initial_value"),
    configuration: { type: widgetType } as WidgetConfiguration,
  };

  if (!widgetType) {
    return (
      <PickWidgetDialog open={open} onOpenChange={onOpenChange} onPick={setPickedType} />
    );
  }

  return (
    <DynamicWidgetDialog
      type={widgetType}
      open={open}
      onOpenChange={onOpenChange}
      onReset={handleReset}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      isCreate
    />
  );
};

export default CreateWidgetDialog;
